
import { useState } from 'react'
import { Calendar, TrendingDown, Target, Activity } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserDietProfileForm } from './user-diet-profile-form'
import { useUserDietProfile } from '@/api/user-diet-profiles'
import { useUserCalendarAssignments } from '@/api/diet-plans-v2'
import { format } from 'date-fns'

interface UserDietOverviewProps {
  userId: string
}

export function UserDietOverview({ userId }: UserDietOverviewProps) {
  const [showProfileForm, setShowProfileForm] = useState(false)
  const { data: profile, isLoading: isLoadingProfile } = useUserDietProfile(userId)
  const { data: assignments = [], isLoading: isLoadingAssignments } = useUserCalendarAssignments(userId)

  const activeAssignments = assignments.filter(a => a.status === 'active')
  const completedAssignments = assignments.filter(a => a.status === 'completed')

  if (isLoadingProfile) {
    return <div className="text-center py-8">Loading diet profile...</div>
  }

  if (!profile && !showProfileForm) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Diet Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            This user hasn't set up their diet profile yet. Create one to get started with personalized nutrition plans.
          </p>
          <Button onClick={() => setShowProfileForm(true)}>
            Create Diet Profile
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (showProfileForm || !profile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Diet Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <UserDietProfileForm userId={userId} onSuccess={() => setShowProfileForm(false)} />
        </CardContent>
      </Card>
    )
  }

  // Calculate progress percentage
  const weightLost = profile.currentWeight - profile.targetWeight
  const initialWeight = profile.weightLogs?.[0]?.weight || profile.currentWeight
  const totalToLose = initialWeight - profile.targetWeight
  const progressPercent = totalToLose > 0 ? Math.round(((initialWeight - profile.currentWeight) / totalToLose) * 100) : 0

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Weight</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{profile.currentWeight} kg</div>
            <p className="text-xs text-muted-foreground">BMI: {profile.bmi}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Target Weight</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{profile.targetWeight} kg</div>
            <p className="text-xs text-muted-foreground">{Math.abs(weightLost).toFixed(1)} kg to go</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Daily Calories</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{profile.tdee}</div>
            <p className="text-xs text-muted-foreground">TDEE (maintenance)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Plans</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeAssignments.length}</div>
            <p className="text-xs text-muted-foreground">{completedAssignments.length} completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Weight Loss Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress towards goal</span>
              <span className="font-medium">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Starting Weight</div>
              <div className="font-medium">{initialWeight} kg</div>
            </div>
            <div>
              <div className="text-muted-foreground">Goal Type</div>
              <div className="font-medium capitalize">{profile.goalType.replace('-', ' ')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="active">
        <TabsList>
          <TabsTrigger value="active">Active Plans</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="profile">Profile Details</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>Active Diet Plans</CardTitle>
            </CardHeader>
            <CardContent>
              {activeAssignments.length === 0 ? (
                <p className="text-muted-foreground">No active diet plans. Assign a plan to get started!</p>
              ) : (
                <div className="space-y-4">
                  {activeAssignments.map((assignment) => (
                    <div key={assignment._id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{assignment.dietPlanName}</h4>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(assignment.startDate), 'PP')} - {format(new Date(assignment.endDate), 'PP')}
                          </p>
                        </div>
                        <Badge>Active</Badge>
                      </div>
                      {assignment.progress && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Days completed: {assignment.progress.daysCompleted} / {assignment.progress.totalDays}</span>
                            <span>{assignment.progress.adherenceRate}% adherence</span>
                          </div>
                          <Progress 
                            value={(assignment.progress.daysCompleted / assignment.progress.totalDays) * 100} 
                            className="h-2" 
                          />
                        </div>
                      )}
                      {assignment.notes && (
                        <p className="text-sm text-muted-foreground">Note: {assignment.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          <Card>
            <CardHeader>
              <CardTitle>Completed Diet Plans</CardTitle>
            </CardHeader>
            <CardContent>
              {completedAssignments.length === 0 ? (
                <p className="text-muted-foreground">No completed plans yet.</p>
              ) : (
                <div className="space-y-4">
                  {completedAssignments.map((assignment) => (
                    <div key={assignment._id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{assignment.dietPlanName}</h4>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(assignment.startDate), 'PP')} - {format(new Date(assignment.endDate), 'PP')}
                          </p>
                        </div>
                        <Badge className="bg-blue-500">Completed</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Profile Details</CardTitle>
                <Button variant="outline" onClick={() => setShowProfileForm(true)}>
                  Edit Profile
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Age</div>
                  <div className="font-medium">{profile.age} years</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Height</div>
                  <div className="font-medium">{profile.height} {profile.heightUnit}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Gender</div>
                  <div className="font-medium capitalize">{profile.gender}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Activity Level</div>
                  <div className="font-medium capitalize">{profile.activityLevel.replace('-', ' ')}</div>
                </div>
              </div>

              {(profile.allergens?.length ?? 0) > 0 && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Allergens</div>
                  <div className="flex flex-wrap gap-2">
                    {profile.allergens.map((allergen) => (
                      <Badge key={allergen} variant="destructive">{allergen}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {(profile.nonPreferredFoods?.length ?? 0) > 0 && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Non-Preferred Foods</div>
                  <div className="flex flex-wrap gap-2">
                    {profile.nonPreferredFoods.map((food) => (
                      <Badge key={food} variant="secondary">{food}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {profile.dietaryRestriction && profile.dietaryRestriction !== 'none' && (
                <div>
                  <div className="text-sm text-muted-foreground">Dietary Restriction</div>
                  <div className="font-medium capitalize">{profile.dietaryRestriction}</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
